/* eslint-disable react-native/no-unused-styles */

import React, { type ComponentProps,useEffect, useRef } from 'react';
import { StyleSheet, type View, type ViewStyle } from 'react-native';

import { useResponsive } from '../../../../../hooks';
import { IconLink } from '../../../../Icon';
import {
  ICON_CONTAINER_SIZE_MD,
  ICON_CONTAINER_SIZE_SM,
  ICON_CONTAINER_SIZE_XS,
  ICON_SIZE_MD,
  ICON_SIZE_SM,
  ICON_SIZE_XS,
  QUANTITY_STEPPER_SIZE_MD,
  QUANTITY_STEPPER_SIZE_SM,
  QUANTITY_STEPPER_SIZE_XS,
} from '../../../IngredientCardV2.constants';

// ─────────────────────────────────────────────────────────────────────────────

export const IngredientCardQuantityStepperButton = (
  props: IngredientCardQuantityStepperButtonProps,
) => {
  const {
    variation,
    accessibilityLabel,
    palette,
    size,
    disabled,
    autoFocus,
    onPress,
  } = props;

  const ref = useRef<View>(null);

  const { match } = useResponsive();

  // ─────────────────────────────────────────────────────────────────────

  const selectedSize = size ?? match(['xs', 'sm']);
  const iconContainerSize = ICON_CONTAINER_SIZES[selectedSize];
  const iconSize = ICON_SIZES[selectedSize];
  const iconStyles = iconSizeStyles[selectedSize];
  const iconPalette: IconPalette = palette === 'spinach' ? 'kale' : 'dark-kale';

  // ─── Effects ─────────────────────────────────────────────────────────

  useEffect(() => {
    if (!autoFocus) return;

    try {
      ref.current?.focus();
    } catch {}
  }, [autoFocus]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <IconLink
      ref={ref}
      style={iconStyles}
      name={variation === 'increase' ? 'IconPlus2' : 'IconMinus2'}
      palette={iconPalette}
      width={iconContainerSize}
      height={iconContainerSize}
      iconSize={iconSize}
      accessibilityLabel={accessibilityLabel}
      disabled={disabled}
      accessibilityRole="button"
      onPress={onPress}
    />
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const ICON_CONTAINER_SIZES: Record<Size, number> = {
  xs: ICON_CONTAINER_SIZE_XS,
  sm: ICON_CONTAINER_SIZE_SM,
  md: ICON_CONTAINER_SIZE_MD,
};

const ICON_SIZES: Record<Size, number> = {
  xs: ICON_SIZE_XS,
  sm: ICON_SIZE_SM,
  md: ICON_SIZE_MD,
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const iconSizeStyles = StyleSheet.create<Record<Size, ViewStyle>>({
  xs: {
    width: QUANTITY_STEPPER_SIZE_XS,
    height: QUANTITY_STEPPER_SIZE_XS,
  },
  sm: {
    width: QUANTITY_STEPPER_SIZE_SM,
    height: QUANTITY_STEPPER_SIZE_SM,
  },
  md: {
    width: QUANTITY_STEPPER_SIZE_MD,
    height: QUANTITY_STEPPER_SIZE_MD,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type IngredientCardQuantityStepperButtonProps = {
  accessibilityLabel: string;
  variation: 'increase' | 'decrease';
  palette?: 'kale' | 'spinach';
  size?: Size;
  disabled?: boolean;
  autoFocus?: boolean;
  onPress: () => void;
};

type Size = 'xs' | 'sm' | 'md';

type IconPalette = ComponentProps<typeof IconLink>['palette'];
